import { useRef, useCallback, useState, useEffect, memo } from "react";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import {
  fotoYaEditadaPorMarco,
  descargarFotoMultimedia,
  descargarFotosZip,
  nombreZipFotosPropiedad,
  urlFotoGrid,
  urlFotoCompleta,
} from "../../utils/fotoMultimediaUtils";
import { crearMiniaturaDesdeFile, revocarMiniaturaUrl } from "../../utils/fotoThumbUtils";
import { captacionUploadQueue } from "../../services/captacionUploadQueue";
import FotoSortableGrid from "./FotoSortableGrid";
import usePermiso from "../../hooks/usePermiso";
import { usePropiedadUi } from "../../hooks/usePropiedadUi";

const UUID_FOTO_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MIME_VIDEO_OK = ["video/mp4", "video/quicktime", "video/webm"];

function IconoAmpliar({ className = "w-3 h-3" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
    </svg>
  );
}

function IconoPlay({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function BtnAccionMini({ onClick, title, children, className = "bg-black/70 text-white hover:bg-black/90" }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`rounded-full w-5 h-5 text-xs flex items-center justify-center transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

const FotoGridItem = memo(function FotoGridItem({
  foto,
  index,
  src,
  onEliminar,
  onVerGrande,
  onCambiarTipo,
  onCambiarDesc,
  mostrarMarcoAutoenhance,
  onCambiarMarcoAutoenhance,
  onDescargarFoto,
  referenciaDescarga = "",
  seleccionada = false,
  onToggleSeleccion,
  modoSeleccion = false,
  compact = false,
  dragAttributes = null,
  dragListeners = null,
  puedeEliminar = true,
  puedeVerDestacar = true,
  puedeModificarDestacar = true,
  puedeVerRetocarMarco = true,
}) {
  const yaEditada = fotoYaEditadaPorMarco(foto);
  const dragProps = compact && dragListeners ? { ...dragAttributes, ...dragListeners } : {};
  return (
    <div
      data-foto-id={foto.id}
      data-foto-index={index}
      className="relative group [contain:layout]"
    >
      <div
        className={
          "aspect-square select-none touch-none "
          + (compact ? "cursor-grab active:cursor-grabbing" : "")
        }
        {...dragProps}
      >
        <img
          data-foto-thumb
          draggable={false}
          loading="lazy"
          decoding="async"
          src={src}
          alt=""
          className="w-full h-full object-cover rounded-lg border border-gray-200 pointer-events-none select-none"
        />
        <div className="absolute top-1 left-1 flex items-start gap-0.5 pointer-events-none">
          {!compact && (
            <label
              className={`pointer-events-auto flex items-center justify-center w-5 h-5 rounded bg-white/95 border shadow-sm cursor-pointer ${modoSeleccion || seleccionada ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              title={seleccionada ? "Desmarcar" : "Marcar"}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={!!seleccionada}
                onChange={() => onToggleSeleccion?.(foto.id)}
                className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer"
              />
            </label>
          )}
          <div className="bg-black bg-opacity-60 text-white text-xs rounded px-1 py-0.5 font-bold" data-foto-num>
            {index + 1}
          </div>
        </div>
        {compact && (
          <div className="absolute bottom-1 left-1 right-1 text-center text-[10px] text-white font-semibold drop-shadow pointer-events-none">
            ⋮⋮
          </div>
        )}
      </div>
      {!compact && (
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100" data-foto-actions>
        <BtnAccionMini
          title="Descargar foto"
          className="bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={(e) => {
            e.stopPropagation();
            onDescargarFoto(index);
          }}
        >
          ↓
        </BtnAccionMini>
        <BtnAccionMini
          title="Ver en grande"
          className="bg-blue-600 text-white hover:bg-blue-700"
          onClick={(e) => {
            e.stopPropagation();
            onVerGrande(index);
          }}
        >
          <IconoAmpliar />
        </BtnAccionMini>
        {puedeEliminar && (
        <BtnAccionMini
          title="Eliminar"
          className="bg-red-500 text-white hover:bg-red-600"
          onClick={(e) => {
            e.stopPropagation();
            onEliminar(foto.id);
          }}
        >
          ×
        </BtnAccionMini>
        )}
      </div>
      )}
      {!compact && (
      <div
        data-foto-portada
        className="absolute bottom-7 left-0 right-0 bg-blue-600 text-white text-xs text-center rounded-b py-0.5"
        style={{ display: index === 0 ? "" : "none" }}
      >
        Portada
      </div>
      )}
      {!compact && puedeVerDestacar && (
      <>
      <select
        data-foto-meta
        value={foto.tipo}
        disabled={!puedeModificarDestacar}
        onChange={(e) => onCambiarTipo(foto.id, e.target.value)}
        className={`w-full mt-1 px-1 py-0.5 border border-gray-200 rounded text-xs bg-white${puedeModificarDestacar ? "" : " opacity-60 cursor-not-allowed"}`}
      >
        <option value="destacada">Destacada</option>
        <option value="publica">Pública</option>
        <option value="privada">Privada</option>
      </select>
      <input
        data-foto-meta
        type="text"
        value={foto.descripcion_es || ""}
        disabled={!puedeModificarDestacar}
        onChange={(e) => onCambiarDesc(foto.id, e.target.value)}
        placeholder="Descripción"
        className={`w-full mt-1 px-1 py-0.5 border border-gray-200 rounded text-xs bg-white${puedeModificarDestacar ? "" : " opacity-60 cursor-not-allowed"}`}
      />
      </>
      )}
      {!compact && mostrarMarcoAutoenhance && puedeVerRetocarMarco && (
        <label
          data-foto-meta
          className={`mt-1 flex items-center gap-1 text-[10px] select-none ${yaEditada ? "text-gray-400 cursor-default" : "text-gray-600 cursor-pointer"}`}
          title={
            yaEditada
              ? "Foto ya editada por Marco — no se volverá a retocar"
              : "Si está marcado, Marco retocará esta foto (16:9, compresión) y la añadirá a la carpeta web de la referencia"
          }
        >
          <input
            type="checkbox"
            checked={yaEditada ? false : foto.marco_autoenhance !== false}
            disabled={yaEditada}
            onChange={(e) => !yaEditada && onCambiarMarcoAutoenhance(foto.id, e.target.checked)}
            className="rounded border-gray-300 disabled:opacity-50"
          />
          {yaEditada ? "Ya editada (Marco)" : "Retocar (Marco)"}
        </label>
      )}
    </div>
  );
});

function SeccionMultimedia(props) {
  const {
    fotos,
    setFotos,
    videosArchivo,
    setVideosArchivo,
    videosYoutube,
    setVideosYoutube,
    form,
    cambiar,
    mostrarMarcoAutoenhance = false,
    captacionId = null,
    propiedadId = null,
    onFotoEliminadaPersistida = null,
  } = props;
  const { tv } = usePropiedadUi(props);
  const { puede } = usePermiso();
  const puedeVerFotos = puede("fotos", "ver");
  const puedeModificarFotos = puede("fotos", "modificar");
  const puedeVerFotosMarcarTodas = puede("fotos_marcar_todas", "ver");
  const puedeVerFotosEliminarSel = puede("fotos_eliminar_sel", "ver");
  const puedeVerFotosDescargar = puede("fotos_descargar", "ver");
  const puedeVerFotosReordenar = puede("fotos_reordenar", "ver");
  const puedeModificarFotosReordenar = puede("fotos_reordenar", "modificar");
  const puedeVerFotosDestacar = puede("fotos_destacar", "ver");
  const puedeModificarFotosDestacar = puede("fotos_destacar", "modificar");
  const puedeVerFotosRetocarMarco = puede("fotos_retocar_marco", "ver");
  const puedeVerVideos = puede("videos", "ver");
  const puedeModificarVideos = puede("videos", "modificar");
  const puedeVerVideosGenerarMarco = puede("videos_generar_marco", "ver");
  const puedeVerVideosYoutube = puede("videos_youtube", "ver");
  const puedeModificarVideosYoutube = puede("videos_youtube", "modificar");
  const puedeVerVisitaVirtual = puede("visita_virtual", "ver");
  const puedeModificarVisitaVirtual = puede("visita_virtual", "modificar");
  const inputRef = useRef(null);
  const inputVideoRef = useRef(null);
  const [modoReordenar, setModoReordenar] = useState(false);
  const [importandoFotos, setImportandoFotos] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [arrastrandoVideo, setArrastrandoVideo] = useState(false);
  const [nuevoVideo, setNuevoVideo] = useState("");
  const [lightboxFotoIdx, setLightboxFotoIdx] = useState(null);
  const [visorVideoSrc, setVisorVideoSrc] = useState(null);
  const [descargandoZip, setDescargandoZip] = useState(false);
  const [regenerandoVideo, setRegenerandoVideo] = useState(false);
  const [fotosSeleccionadas, setFotosSeleccionadas] = useState(() => new Set());
  const visorVideoRef = useRef(null);

  const esVideoMarco = (v) => {
    const t = String(v?.titulo || "");
    const u = String(v?.url || v?.preview || "");
    return /marco/i.test(t) || /marco-videos\//i.test(u);
  };
  const tieneVideoMarco = videosArchivo.some(esVideoMarco);

  const regenerarVideoMarco = async () => {
    if (!propiedadId || regenerandoVideo) return;
    if (!fotos.length) {
      dialogs.alert("No hay fotos en la propiedad para generar el vídeo.");
      return;
    }
    const ok = await dialogs.confirm(
      tieneVideoMarco
        ? "Se regenerará el Vídeo Marco con las fotos actuales de la ficha. El vídeo anterior se eliminará del CRM, la nube y el NAS. La generación tarda unos minutos. ¿Continuar?"
        : "Se generará el Vídeo Marco con las fotos actuales de la ficha. La generación tarda unos minutos. ¿Continuar?",
    );
    if (!ok) return;
    setRegenerandoVideo(true);
    try {
      const { data } = await api.post(`/propiedades/${propiedadId}/videos/regenerar-marco`);
      if (!data?.ok) throw new Error(data?.error || "No se pudo generar el vídeo");

      // Si ya vino listo (legado síncrono), refrescar lista; si está encolado, avisar.
      if (!data.encolado) {
        const r = await api.get(`/propiedades/${propiedadId}/videos`);
        const raw = r.data?.data || [];
        const apiOrigin = (import.meta.env.VITE_API_URL || "http://localhost:4000").replace(/\/$/, "");
        const arch = [];
        for (const v of raw) {
          if (String(v.tipo || "youtube").toLowerCase() !== "archivo") continue;
          const u = v.url || "";
          const fullUrl = u.startsWith("http") ? u : `${apiOrigin}${u.startsWith("/") ? "" : "/"}${u}`;
          arch.push({
            id: v.id || crypto.randomUUID(),
            url: fullUrl,
            titulo: v.titulo || "",
            existente: true,
            preview: fullUrl,
          });
        }
        setVideosArchivo(arch);
      }

      if (data.encolado) {
        dialogs.alert(
          (data.creado || !tieneVideoMarco
            ? "Generación del Vídeo Marco en marcha"
            : "Regeneración del Vídeo Marco en marcha")
            + ` con ${data.fotosDisponibles || fotos.length} foto(s). `
            + "En unos minutos aparecerá en esta sección; recarga la ficha si no lo ves.",
        );
      } else {
        dialogs.alert(
          data.fotosUsadas
            ? `Vídeo listo con ${data.fotosUsadas} foto(s).`
            : "Vídeo generado correctamente.",
        );
      }
    } catch (err) {
      dialogs.alert(err?.response?.data?.error || err.message || "Error al generar el vídeo");
    } finally {
      setRegenerandoVideo(false);
    }
  };

  useEffect(() => {
    setFotosSeleccionadas((prev) => {
      const ids = new Set(fotos.map((f) => f.id));
      const next = new Set([...prev].filter((id) => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [fotos]);

  const srcFotoGrid = (foto) => urlFotoGrid(foto);
  const srcFotoCompleta = (foto) => urlFotoCompleta(foto);
  const srcVideo = (v) => v?.preview || v?.url || "";

  useEffect(() => {
    if (lightboxFotoIdx === null && !visorVideoSrc) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightboxFotoIdx(null);
        setVisorVideoSrc(null);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxFotoIdx, visorVideoSrc]);

  useEffect(() => {
    if (!visorVideoSrc) return;
    const el = visorVideoRef.current;
    if (!el) return;
    const tid = window.setTimeout(() => {
      el.play().catch(() => {
        el.muted = true;
        el.play().catch(() => {});
      });
    }, 0);
    return () => window.clearTimeout(tid);
  }, [visorVideoSrc]);

  const dragItemVid = useRef(null);
  const dragOverVid = useRef(null);

  useEffect(() => {
    if (lightboxFotoIdx === null || !fotos.length) return;
    const n = fotos.length;
    [-1, 1].forEach((delta) => {
      const idx = (lightboxFotoIdx + delta + n) % n;
      const url = urlFotoCompleta(fotos[idx]);
      if (!url || url.startsWith("blob:")) return;
      const img = new Image();
      img.src = url;
    });
  }, [lightboxFotoIdx, fotos]);

  const procesar = async (archivos) => {
    const ok = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const files = Array.from(archivos || []).filter((f) => ok.includes(f.type));
    if (!files.length) return;
    setImportandoFotos(true);
    const nuevas = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        let thumbPreview = null;
        try {
          thumbPreview = await crearMiniaturaDesdeFile(f);
        } catch {
          thumbPreview = URL.createObjectURL(f);
        }
        nuevas.push({
          file: f,
          preview: thumbPreview,
          thumbPreview,
          id: crypto.randomUUID(),
          tipo: "publica",
          descripcion_es: "",
          orden: fotos.length + nuevas.length,
          marco_autoenhance: true,
        });
      }
      setFotos((prev) => [...prev, ...nuevas]);
    } finally {
      setImportandoFotos(false);
    }
  };

  const procesarVideos = (archivos) => {
    const nuevas = [];
    Array.from(archivos).forEach((f) => {
      if (!MIME_VIDEO_OK.includes(f.type)) return;
      nuevas.push({
        file: f,
        preview: URL.createObjectURL(f),
        id: crypto.randomUUID(),
        titulo: "",
        existente: false,
      });
    });
    setVideosArchivo((prev) => [...prev, ...nuevas]);
  };

  const esDropReordenFoto = () => modoReordenar;

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setArrastrando(false);
      if (esDropReordenFoto()) return;
      if (e.dataTransfer.files?.length) procesar(e.dataTransfer.files);
    },
    [fotos]
  );

  const onDropVideos = useCallback((e) => {
    e.preventDefault();
    setArrastrandoVideo(false);
    procesarVideos(e.dataTransfer.files);
  }, []);

  const onDragStartVid = (i) => {
    dragItemVid.current = i;
  };
  const onDragOverVid = (e, i) => {
    e.preventDefault();
    dragOverVid.current = i;
  };
  const onDropVid = (e) => {
    e.preventDefault();
    if (dragItemVid.current === null || dragOverVid.current === null) return;
    const n = [...videosArchivo];
    const [m] = n.splice(dragItemVid.current, 1);
    n.splice(dragOverVid.current, 0, m);
    dragItemVid.current = null;
    dragOverVid.current = null;
    setVideosArchivo(n);
  };

  const elimFoto = useCallback(async (id) => {
    const f = fotos.find((x) => x.id === id);
    if (captacionId) captacionUploadQueue.removeJob(captacionId, "foto", id);
    if (propiedadId && f?.existente && UUID_FOTO_RX.test(String(id))) {
      try {
        await api.delete(`/fotos/${id}`);
      } catch {
        /* se reintentará al guardar (fotos_orden) */
      }
    } else if (captacionId && f?.existente && UUID_FOTO_RX.test(String(id))) {
      try {
        await api.delete(`/captaciones/fotos-reportaje/${id}`);
        onFotoEliminadaPersistida?.(id);
      } catch {
        /* se reintentará al guardar borrador */
      }
    }
    setFotos((prev) => {
      const foto = prev.find((x) => x.id === id);
      revocarMiniaturaUrl(foto?.thumbPreview);
      if (foto?.preview && foto?.file && foto.preview !== foto.thumbPreview) {
        revocarMiniaturaUrl(foto.preview);
      }
      return prev.filter((x) => x.id !== id);
    });
    setFotosSeleccionadas((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, [captacionId, propiedadId, fotos, setFotos, onFotoEliminadaPersistida]);

  const toggleSeleccionFoto = useCallback((id) => {
    setFotosSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const todasSeleccionadas = fotos.length > 0 && fotos.every((f) => fotosSeleccionadas.has(f.id));
  const algunaSeleccionada = fotosSeleccionadas.size > 0;

  const toggleSeleccionTodas = useCallback(() => {
    if (todasSeleccionadas) {
      setFotosSeleccionadas(new Set());
    } else {
      setFotosSeleccionadas(new Set(fotos.map((f) => f.id)));
    }
  }, [fotos, todasSeleccionadas]);

  const eliminarSeleccionadas = useCallback(async () => {
    const ids = [...fotosSeleccionadas];
    if (!ids.length) return;
    const ok = await dialogs.confirm(`¿Eliminar ${ids.length} foto${ids.length === 1 ? "" : "s"} seleccionada${ids.length === 1 ? "" : "s"}?`);
    if (!ok) return;
    for (const id of ids) {
      await elimFoto(id);
    }
    setFotosSeleccionadas(new Set());
  }, [fotosSeleccionadas, elimFoto]);

  const handleCambiarTipo = useCallback((id, tipo) => {
    setFotos((prev) => prev.map((f) => (f.id === id ? { ...f, tipo } : f)));
  }, [setFotos]);

  const handleCambiarDesc = useCallback((id, descripcion_es) => {
    setFotos((prev) => prev.map((f) => (f.id === id ? { ...f, descripcion_es } : f)));
  }, [setFotos]);

  const elimVideoArchivo = (id) => {
    setVideosArchivo((prev) => {
      const v = prev.find((x) => x.id === id);
      if (v?.preview && v?.file) URL.revokeObjectURL(v.preview);
      return prev.filter((x) => x.id !== id);
    });
  };

  const addVideoYoutube = () => {
    if (!nuevoVideo.trim()) return;
    setVideosYoutube([...videosYoutube, { url: nuevoVideo.trim(), titulo: "", id: crypto.randomUUID(), tipo: "youtube" }]);
    setNuevoVideo("");
  };

  const abrirFotos = () => inputRef.current?.click();
  const abrirVideos = () => inputVideoRef.current?.click();

  const handleDescargarFoto = useCallback(async (index) => {
    const foto = fotos[index];
    if (!foto) return;
    try {
      await descargarFotoMultimedia(foto, index, form.referencia || "fotos");
    } catch {
      window.alert("No se pudo descargar la foto.");
    }
  }, [fotos, form.referencia]);

  const fotosParaDescarga = algunaSeleccionada
    ? fotos.filter((f) => fotosSeleccionadas.has(f.id))
    : fotos;

  const handleDescargarZip = useCallback(async () => {
    if (!fotosParaDescarga.length || descargandoZip) return;
    setDescargandoZip(true);
    const ref = form.referencia || "fotos";
    const fin = () => window.setTimeout(() => setDescargandoZip(false), 4000);
    const esSubconjunto = algunaSeleccionada && fotosParaDescarga.length < fotos.length;
    const nombreZip = esSubconjunto
      ? `${String(ref).replace(/[^\w.-]+/g, "_").slice(0, 40)}_fotos_sel.zip`
      : nombreZipFotosPropiedad(ref);

    const avisarDescargaOk = (nombre) => {
      window.setTimeout(() => {
        dialogs.alert(
          `Se está descargando «${nombre}» (${fotosParaDescarga.length} foto${fotosParaDescarga.length === 1 ? "" : "s"}).\n\n`
          + (esSubconjunto
            ? "Solo las fotos marcadas van en este ZIP."
            : "Las fotos se recomprimen a ~1280 px para que el ZIP quepa en adjuntos de hasta 20 MB."),
          { autoCloseMs: 12000 },
        );
      }, 1500);
    };

    try {
      const nombre = await descargarFotosZip(fotosParaDescarga, ref, propiedadId, {
        nombreZip,
        totalFotos: fotos.length,
      });
      if (nombre) avisarDescargaOk(nombre);
    } catch {
      window.alert("No se pudo crear el archivo comprimido.");
    } finally {
      fin();
    }
  }, [fotosParaDescarga, fotos.length, form.referencia, propiedadId, descargandoZip, algunaSeleccionada]);

  const handleCambiarMarcoAutoenhance = useCallback((fotoId, checked) => {
    setFotos((prev) =>
      prev.map((f) => (f.id === fotoId ? { ...f, marco_autoenhance: checked } : f)),
    );
    const isUuid = typeof fotoId === "string" && /^[0-9a-f-]{36}$/i.test(fotoId);
    if (!isUuid) return;
    if (captacionId) {
      api
        .put(`/captaciones/${captacionId}/fotos-reportaje/marco-autoenhance`, {
          flags: [{ id: fotoId, marco_autoenhance: checked }],
        })
        .catch(() => {});
      return;
    }
    if (propiedadId) {
      api
        .put(`/propiedades/${propiedadId}/fotos/marco-autoenhance`, {
          flags: [{ id: fotoId, marco_autoenhance: checked }],
        })
        .catch(() => {});
    }
  }, [captacionId, propiedadId, setFotos]);

  const zonaSubirSt =
    "border-2 border-dashed rounded-xl transition-all cursor-pointer text-center select-none " +
    "border-gray-400 hover:border-blue-500 hover:bg-blue-50/50 bg-white";

  return (
    <div>
      <style>{`.foto-grid-reordenando{user-select:none}.foto-grid-reordenando [data-foto-meta],.foto-grid-reordenando [data-foto-actions]{display:none!important}`}</style>
      <h2 className="font-bold text-gray-700 mb-4 text-sm uppercase tracking-wide">{tv("Multimedia")}</h2>
      {puedeVerFotos && (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-600">{tv("Fotos")} ({fotos.length})</h3>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {fotos.length > 0 && (
            <>
              {puedeVerFotosMarcarTodas && (
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none px-2 py-1.5 rounded-lg border border-gray-200 bg-white">
                <input
                  type="checkbox"
                  checked={todasSeleccionadas}
                  onChange={toggleSeleccionTodas}
                  className="rounded border-gray-300"
                />
                {todasSeleccionadas ? tv("Desmarcar todas") : tv("Marcar todas")}
              </label>
              )}
              {algunaSeleccionada && puedeVerFotosEliminarSel && (
                <button
                  type="button"
                  onClick={eliminarSeleccionadas}
                  className="text-sm px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg font-medium hover:bg-red-100 shrink-0"
                >
                  {tv("Eliminar")} ({fotosSeleccionadas.size})
                </button>
              )}
              {puedeVerFotosDescargar && (
              <button
                type="button"
                onClick={handleDescargarZip}
                disabled={descargandoZip}
                className="text-sm px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-60 shrink-0"
                title={algunaSeleccionada ? "Descargar fotos marcadas en ZIP" : "Descargar todas las fotos en ZIP"}
              >
                {descargandoZip
                  ? tv("Preparando…")
                  : algunaSeleccionada
                    ? `↓ ${tv("Descargar")} (${fotosSeleccionadas.size})`
                    : `↓ ${tv("Descargar fotos")}`}
              </button>
              )}
            </>
          )}
              {puedeVerFotosReordenar && (
              <button
                type="button"
                onClick={() => { if (puedeModificarFotosReordenar) setModoReordenar((v) => !v); }}
                disabled={!puedeModificarFotosReordenar}
                title={!puedeModificarFotosReordenar ? "No tienes permiso para reordenar fotos" : undefined}
                className={
                  "text-sm px-3 py-1.5 rounded-lg font-medium shrink-0 border "
                  + (modoReordenar
                    ? "bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50")
                  + (puedeModificarFotosReordenar ? "" : " opacity-60 cursor-not-allowed")
                }
              >
                {modoReordenar ? tv("✓ Listo (reordenar)") : tv("⇅ Reordenar fotos")}
              </button>
              )}
              <button
                type="button"
                onClick={abrirFotos}
                disabled={importandoFotos || modoReordenar || !puedeModificarFotos}
                title={!puedeModificarFotos ? "No tienes permiso para añadir fotos" : undefined}
                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 shrink-0 disabled:opacity-60"
              >
                {importandoFotos ? tv("Preparando…") : `+ ${tv("Buscar fotos")}`}
              </button>
        </div>
        </div>
        <div
          onClick={fotos.length === 0 ? (puedeModificarFotos ? abrirFotos : undefined) : undefined}
          onDrop={puedeModificarFotos ? onDrop : (e) => e.preventDefault()}
          onDragOver={(e) => {
            e.preventDefault();
            if (puedeModificarFotos) setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          className={
            "border-2 border-dashed rounded-xl transition-all " +
            (arrastrando ? "border-blue-400 bg-blue-50" : "border-gray-400 hover:border-gray-500 bg-white") +
            (fotos.length === 0 ? " cursor-pointer p-10 text-center" : " p-4") +
            (puedeModificarFotos ? "" : " opacity-70 cursor-not-allowed")
          }
        >
          {fotos.length === 0 ? (
            <div>
              <p className="text-3xl mb-2">📷</p>
              <p className="font-semibold text-gray-600 text-sm">{tv("Arrastra fotos aquí o haz click")}</p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP</p>
            </div>
          ) : (
            <FotoSortableGrid
              fotos={fotos}
              setFotos={setFotos}
              modoReordenar={modoReordenar}
              srcForFoto={srcFotoGrid}
              renderItem={(foto, i, drag) => (
                <FotoGridItem
                  key={foto.id}
                  foto={foto}
                  index={i}
                  src={srcFotoGrid(foto)}
                  onEliminar={elimFoto}
                  onVerGrande={setLightboxFotoIdx}
                  onCambiarTipo={handleCambiarTipo}
                  onCambiarDesc={handleCambiarDesc}
                  mostrarMarcoAutoenhance={mostrarMarcoAutoenhance}
                  onCambiarMarcoAutoenhance={handleCambiarMarcoAutoenhance}
                  onDescargarFoto={handleDescargarFoto}
                  referenciaDescarga={form.referencia}
                  seleccionada={fotosSeleccionadas.has(foto.id)}
                  onToggleSeleccion={toggleSeleccionFoto}
                  modoSeleccion={algunaSeleccionada}
                  compact={drag.compact}
                  dragAttributes={drag.dragAttributes}
                  dragListeners={drag.dragListeners}
                  puedeEliminar={puedeModificarFotos}
                  puedeVerDestacar={puedeVerFotosDestacar}
                  puedeModificarDestacar={puedeModificarFotosDestacar}
                  puedeVerRetocarMarco={puedeVerFotosRetocarMarco}
                />
              )}
            />
          )}
        </div>
        {fotos.length > 0 && (
          <>
            <p className="text-xs text-gray-400 mt-2">
              {modoReordenar
                ? "Modo reordenar: arrastra las fotos. Pulsa «Listo» cuando termines. La primera es portada."
                : "Pulsa «Reordenar fotos» para cambiar el orden. La primera es portada."}
            </p>
            {mostrarMarcoAutoenhance && puedeVerFotosRetocarMarco && (
              <p className="text-xs text-amber-700 mt-1">
                «Retocar (Marco)»: al guardar, Marco procesará la foto y la añadirá a la carpeta web de la referencia en el NAS.
              </p>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={puedeModificarFotos ? abrirFotos : undefined}
              onKeyDown={(e) => { if (puedeModificarFotos && e.key === "Enter") abrirFotos(); }}
              onDrop={puedeModificarFotos ? onDrop : (e) => e.preventDefault()}
              onDragOver={(e) => { e.preventDefault(); if (puedeModificarFotos) setArrastrando(true); }}
              onDragLeave={() => setArrastrando(false)}
              className={`${zonaSubirSt} mt-3 p-4 ${arrastrando ? "border-blue-400 bg-blue-50" : ""}${puedeModificarFotos ? "" : " opacity-70 cursor-not-allowed"}`}
            >
              <p className="text-sm text-gray-600">
                Arrastra más fotos aquí o{" "}
                <span className="text-blue-600 font-semibold">haz click para buscar en tu equipo</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP</p>
            </div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={!puedeModificarFotos}
          onChange={(e) => { procesar(e.target.files); e.target.value = ""; }}
        />
      </div>
      )}

      {/* Vídeos archivo (R2) — mismo patrón que fotos */}
      {puedeVerVideos && (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-600">{tv("Vídeos")} ({videosArchivo.length})</h3>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            {propiedadId && puedeVerVideosGenerarMarco && (
              <button
                type="button"
                onClick={regenerarVideoMarco}
                disabled={regenerandoVideo || !fotos.length}
                title={
                  !fotos.length
                    ? "Necesitas fotos en la ficha"
                    : tieneVideoMarco
                      ? "Vuelve a generar el Vídeo Marco con las fotos actuales (sustituye el anterior)"
                      : "Genera el Vídeo Marco con las fotos actuales de la ficha"
                }
                className="text-sm px-3 py-1.5 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regenerandoVideo
                  ? (tieneVideoMarco ? tv("Regenerando…") : tv("Generando…"))
                  : (tieneVideoMarco ? tv("Regenerar video") : tv("Generar video"))}
              </button>
            )}
            <button
              type="button"
              onClick={abrirVideos}
              disabled={!puedeModificarVideos}
              title={!puedeModificarVideos ? "No tienes permiso para añadir vídeos" : undefined}
              className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              + {tv("Buscar vídeos")}
            </button>
          </div>
        </div>
        <div
          onClick={videosArchivo.length === 0 ? (puedeModificarVideos ? abrirVideos : undefined) : undefined}
          onDrop={puedeModificarVideos ? onDropVideos : (e) => e.preventDefault()}
          onDragOver={(e) => {
            e.preventDefault();
            if (puedeModificarVideos) setArrastrandoVideo(true);
          }}
          onDragLeave={() => setArrastrandoVideo(false)}
          className={
            "border-2 border-dashed rounded-xl transition-all " +
            (arrastrandoVideo ? "border-blue-400 bg-blue-50" : "border-gray-400 hover:border-gray-500 bg-white") +
            (videosArchivo.length === 0 ? " cursor-pointer p-10 text-center" : " p-4") +
            (puedeModificarVideos ? "" : " opacity-70 cursor-not-allowed")
          }
        >
          {videosArchivo.length === 0 ? (
            <div>
              <p className="text-3xl mb-2">🎬</p>
              <p className="font-semibold text-gray-600 text-sm">Arrastra vídeos aquí o haz click</p>
              <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM · máx. recomendado ~1 min</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" onDrop={onDropVid}>
              {videosArchivo.map((v, i) => (
                <div
                  key={v.id}
                  draggable
                  onDragStart={() => onDragStartVid(i)}
                  onDragOver={(e) => onDragOverVid(e, i)}
                  className="relative group cursor-grab active:cursor-grabbing border border-gray-200 rounded-lg overflow-hidden bg-black"
                >
                  <div
                    className="relative flex items-center justify-center bg-black"
                    style={{ minHeight: 80, maxHeight: 340 }}
                  >
                    <video
                      src={srcVideo(v)}
                      playsInline
                      preload="metadata"
                      muted
                      className="block w-full max-h-[340px] object-contain pointer-events-none"
                    />
                    <button
                      type="button"
                      title="Reproducir vídeo"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVisorVideoSrc(srcVideo(v));
                      }}
                      className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/40 transition-colors group/play"
                    >
                      <span className="w-12 h-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center shadow-lg group-hover/play:scale-105 transition-transform">
                        <IconoPlay />
                      </span>
                    </button>
                  </div>
                  <div className="absolute top-1 left-1 bg-black bg-opacity-60 text-white text-xs rounded px-1 py-0.5 font-bold z-10">
                    {i + 1}
                  </div>
                  {puedeModificarVideos && (
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                    <BtnAccionMini
                      title="Eliminar"
                      className="bg-red-500 text-white hover:bg-red-600 w-5 h-5"
                      onClick={(e) => {
                        e.stopPropagation();
                        elimVideoArchivo(v.id);
                      }}
                    >
                      ×
                    </BtnAccionMini>
                  </div>
                  )}
                  <div className="p-2 bg-white border-t border-gray-100">
                    <input
                      type="text"
                      value={v.titulo || ""}
                      disabled={!puedeModificarVideos}
                      onChange={(e) =>
                        setVideosArchivo((prev) =>
                          prev.map((x) => (x.id === v.id ? { ...x, titulo: e.target.value } : x))
                        )
                      }
                      placeholder="Título (opcional)"
                      className={`w-full px-2 py-1 border border-gray-200 rounded text-xs${puedeModificarVideos ? "" : " opacity-60 cursor-not-allowed"}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {videosArchivo.length > 0 && (
          <>
            <p className="text-xs text-gray-400 mt-2">Arrastra para reordenar. Se suben a la nube al guardar la propiedad.</p>
            <div
              role="button"
              tabIndex={0}
              onClick={puedeModificarVideos ? abrirVideos : undefined}
              onKeyDown={(e) => { if (puedeModificarVideos && e.key === "Enter") abrirVideos(); }}
              onDrop={puedeModificarVideos ? onDropVideos : (e) => e.preventDefault()}
              onDragOver={(e) => { e.preventDefault(); if (puedeModificarVideos) setArrastrandoVideo(true); }}
              onDragLeave={() => setArrastrandoVideo(false)}
              className={`${zonaSubirSt} mt-3 p-4 ${arrastrandoVideo ? "border-blue-400 bg-blue-50" : ""}${puedeModificarVideos ? "" : " opacity-70 cursor-not-allowed"}`}
            >
              <p className="text-sm text-gray-600">
                Arrastra más vídeos aquí o{" "}
                <span className="text-blue-600 font-semibold">haz click para buscar en tu equipo</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">MP4, MOV, WebM</p>
            </div>
          </>
        )}
        <input
          ref={inputVideoRef}
          type="file"
          multiple
          accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
          className="hidden"
          disabled={!puedeModificarVideos}
          onChange={(e) => { procesarVideos(e.target.files); e.target.value = ""; }}
        />
      </div>
      )}

      {puedeVerVideosYoutube && (
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-600 mb-3">{tv("Videos Youtube")}</h3>
        {videosYoutube.map((v) => (
          <div key={v.id} className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-600 flex-1 truncate">{v.url}</span>
            {puedeModificarVideosYoutube && (
            <button
              type="button"
              onClick={() => setVideosYoutube(videosYoutube.filter((x) => x.id !== v.id))}
              className="text-red-500 text-xs"
            >
              {tv("Eliminar")}
            </button>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="url"
            value={nuevoVideo}
            onChange={(e) => setNuevoVideo(e.target.value)}
            placeholder="https://youtube.com/..."
            disabled={!puedeModificarVideosYoutube}
            className={`flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white${puedeModificarVideosYoutube ? "" : " opacity-60 cursor-not-allowed"}`}
          />
          <button
            type="button"
            onClick={addVideoYoutube}
            disabled={!puedeModificarVideosYoutube}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            + {tv("Añadir")}
          </button>
        </div>
      </div>
      )}
      {puedeVerVisitaVirtual && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{tv("Visita Virtual (URL)")}</label>
          <input
            type="url"
            name="visita_virtual_url"
            value={form.visita_virtual_url || ""}
            onChange={cambiar}
            disabled={!puedeModificarVisitaVirtual}
            placeholder="https://..."
            className={`px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white${puedeModificarVisitaVirtual ? "" : " opacity-60 cursor-not-allowed"}`}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">{tv("Visita 360º (URL)")}</label>
          <input
            type="url"
            name="visita_360_url"
            value={form.visita_360_url || ""}
            onChange={cambiar}
            disabled={!puedeModificarVisitaVirtual}
            placeholder="https://..."
            className={`px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white${puedeModificarVisitaVirtual ? "" : " opacity-60 cursor-not-allowed"}`}
          />
        </div>
      </div>
      )}

      {lightboxFotoIdx !== null && fotos[lightboxFotoIdx] && (
        <div
          role="presentation"
          className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/92 p-4"
          onClick={() => setLightboxFotoIdx(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxFotoIdx(null); }}
            className="absolute top-4 right-4 px-3.5 py-2 rounded-lg border-0 cursor-pointer bg-white/15 text-white font-bold text-sm hover:bg-white/25"
          >
            {tv("Cerrar")}
          </button>
          {puedeModificarFotos && (
            <button
              type="button"
              title="Insertar más fotos"
              onClick={(e) => { e.stopPropagation(); abrirFotos(); }}
              className="absolute top-4 right-24 px-3.5 py-2 rounded-lg border-0 cursor-pointer bg-violet-600 text-white font-bold text-sm hover:bg-violet-500"
            >
              + Insertar fotos
            </button>
          )}
          {fotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxFotoIdx((idx) => (idx > 0 ? idx - 1 : fotos.length - 1));
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border-0 cursor-pointer text-xl font-bold bg-white/90 text-gray-900 hover:bg-white"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxFotoIdx((idx) => (idx < fotos.length - 1 ? idx + 1 : 0));
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border-0 cursor-pointer text-xl font-bold bg-white/90 text-gray-900 hover:bg-white"
              >
                ›
              </button>
            </>
          )}
          <img
            src={srcFotoCompleta(fotos[lightboxFotoIdx])}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[96vw] max-h-[90vh] object-contain rounded-lg"
          />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white text-xs font-semibold drop-shadow">
            {lightboxFotoIdx + 1} / {fotos.length}
          </div>
        </div>
      )}

      {visorVideoSrc && (
        <div
          role="presentation"
          className="fixed inset-0 z-[12000] flex items-center justify-center bg-black/92 p-4"
          onClick={() => setVisorVideoSrc(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setVisorVideoSrc(null); }}
            className="absolute top-4 right-4 px-3.5 py-2 rounded-lg border-0 cursor-pointer bg-white/15 text-white font-bold text-sm hover:bg-white/25"
          >
            {tv("Cerrar")}
          </button>
          <video
            ref={visorVideoRef}
            src={visorVideoSrc}
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
            className="max-w-[96vw] max-h-[90vh] rounded-lg bg-black"
          />
        </div>
      )}
    </div>
  );
}

export default memo(SeccionMultimedia, (a, b) =>
  a.fotos === b.fotos &&
  a.videosArchivo === b.videosArchivo &&
  a.videosYoutube === b.videosYoutube &&
  a.setFotos === b.setFotos &&
  a.setVideosArchivo === b.setVideosArchivo &&
  a.setVideosYoutube === b.setVideosYoutube &&
  a.cambiar === b.cambiar &&
  a.mostrarMarcoAutoenhance === b.mostrarMarcoAutoenhance &&
  a.captacionId === b.captacionId &&
  a.propiedadId === b.propiedadId &&
  a.onFotoEliminadaPersistida === b.onFotoEliminadaPersistida &&
  a.form?.visita_virtual_url === b.form?.visita_virtual_url &&
  a.form?.visita_360_url === b.form?.visita_360_url
);
